'use client';
import cn from 'classnames';
import Image from 'next/image';
import { useState } from 'react';

import Info from 'public/img/icon/info.svg';

import './MemberSummaryPanel.style.scss';

import Text from '@/components/common/Text/Text';
import CollapsibleSection from '@/components/common/CollapsibleSection/CollapsibleSection';
import { DUE_NOW_DISPLAY } from '@/const/due_now';
import { RawEPCCCartResponse } from '@/types/Cart';
import { InsuranceProduct, SubscriptionProduct } from '@/types/Subscription';
import { SignupState } from '@/types/Signup';
import { displayTierName } from '@/util/product_client_safe';

import type { ReferralCopyProps } from '@/components/common/SignUpApp/SignUpApp';
import ReferralCodeField from './ReferralCodeField';
import { TierTooltipContent } from './tierTooltips';

/** Base membership tiers, in display order. Add-ons are filtered out. */
const BASE_TIER_SLUGS = [
  'classique',
  'couture',
  'classique-plus',
  'couture-plus'
];

type MemberSummaryPanelProps = {
  className?: string;
  user: SignupState;
  subs: SubscriptionProduct[];
  insuranceData: InsuranceProduct[];
  previewCart?: RawEPCCCartResponse;
  /** False while a tier/add-on/coverage change is being repriced. */
  previewCartUpdated: boolean;
  referralCopy?: ReferralCopyProps;
  onPlanChange: (plan: SubscriptionProduct) => void;
  onAddonChange: (addon: string) => void;
  onCoverageChange: (coverage: string) => void;
  onReferralCommit: (referralCode: string) => void;
};

/**
 * Inline membership summary. Because it lives on the page rather than in a
 * modal, it reads the page's single `previewCart` directly — the multi-page
 * flow's `SignUpSummaryModal` had to mirror five pieces of state and run its
 * own `/api/cart/preview` call purely because modals cannot inherit updated
 * props from their parent.
 */
function MemberSummaryPanel(props: MemberSummaryPanelProps) {
  const {
    className = '',
    user,
    subs,
    insuranceData,
    previewCart,
    previewCartUpdated,
    referralCopy,
    onPlanChange,
    onAddonChange,
    onCoverageChange,
    onReferralCommit
  } = props;

  const [openSection, setOpenSection] = useState<string | null>('tier');
  /**
   * Which tier's info card is showing. Driven off the icon's own hover rather
   * than react-tooltip's anchor detection: the card is large and, positioned
   * left, overlaps the neighbouring info icons, which made the default hover
   * behaviour retrigger siblings and leave several cards stuck open.
   */
  const [hoveredTier, setHoveredTier] = useState<string | null>(null);

  const baseTiers = BASE_TIER_SLUGS.map((slug) =>
    subs.find((sub) => sub.slug === slug)
  ).filter(Boolean) as SubscriptionProduct[];

  const premierAddon = subs.find((sub) => sub.slug === 'premier');
  const premiumInsurance = insuranceData.find((ins) => ins.slug === 'premium');

  const selectedTier = baseTiers.find((tier) => tier.slug === user.plan?.slug);
  const tierLabel = [
    selectedTier && displayTierName(selectedTier.name),
    user.addon_subscription === 'premier' && 'Premier add-on'
  ]
    .filter(Boolean)
    .join(', ');

  const monthly = previewCart?.data?.meta.display_price.without_tax.formatted;

  const toggle = (section: string) => (open: boolean) =>
    setOpenSection(open ? section : null);

  return (
    <div className={cn('MemberSummaryPanel', className)}>
      <Text
        className='MemberSummaryPanel-title'
        variant='title-xxs-700'
        uppercase
      >
        Membership Summary
      </Text>

      {/* Always visible — never collapsible. */}
      <div className='MemberSummaryPanel-cycle'>
        <Text
          variant='title-xxs-500-upper'
          className='MemberSummaryPanel-cycle-title'
        >
          Your billing cycle
        </Text>

        <Text
          variant='text-m'
          className='MemberSummaryPanel-cycle-note'
        >
          Your billing cycle will begin once your membership application has
          been accepted.
        </Text>

        <p className='MemberSummaryPanel-due'>
          <Text
            variant='span-s-500'
            className='MemberSummaryPanel-due-label'
          >
            Tier:
          </Text>{' '}
          <span data-testid='summary-tier'>{tierLabel}</span>
        </p>

        <p className='MemberSummaryPanel-due'>
          <Text
            variant='span-s-500'
            className='MemberSummaryPanel-due-label'
          >
            Coverage:
          </Text>{' '}
          <span data-testid='summary-coverage'>
            {user.insurance_level === 'premium' ? 'Premium' : 'Standard'}
          </span>
        </p>

        <p className='MemberSummaryPanel-due'>
          <Text
            variant='span-s-500'
            className='MemberSummaryPanel-due-label'
          >
            Monthly:
          </Text>{' '}
          {/* Announce the new figure when a tier/add-on/coverage change reprices. */}
          <span
            className={cn('MemberSummaryPanel-price', {
              pending: !previewCartUpdated
            })}
            data-testid='summary-monthly'
            aria-live='polite'
          >
            {monthly}
          </span>
        </p>

        <p className='MemberSummaryPanel-due MemberSummaryPanel-due--now'>
          <Text
            variant='span-s-500'
            className='MemberSummaryPanel-due-label'
          >
            Due Now:
          </Text>{' '}
          <span data-testid='summary-due-now'>{DUE_NOW_DISPLAY}</span>
        </p>
      </div>

      <div className='MemberSummaryPanel-divider' />

      <CollapsibleSection
        title={<Text variant='title-xxs-500-upper'>Membership Tier</Text>}
        open={openSection === 'tier'}
        onRequestChange={toggle('tier')}
        testId='summary-section-tier'
      >
        <fieldset className='MemberSummaryPanel-options'>
          <legend className='MemberSummaryPanel-sublabel'>
            <Text variant='title-xxs-500-upper'>Tiers</Text>
          </legend>
          {baseTiers.map((tier) => (
            <label
              key={tier.slug}
              className='MemberSummaryPanel-option cursor'
            >
              <input
                type='radio'
                name='membership-tier'
                value={tier.slug}
                checked={user.plan?.slug === tier.slug}
                onChange={() => onPlanChange(tier)}
                className='MemberSummaryPanel-radio'
              />
              <Text className='MemberSummaryPanel-option-label'>
                {displayTierName(tier.name)}{' '}
                {tier.price.asFormattedString.split('.')[0]}/month
              </Text>
              {/*
                Hand-rolled hover popover rather than react-tooltip: the cards
                are ~250px tall but the info icons are stacked ~40px apart, so
                every card overlaps its neighbours' anchors. react-tooltip binds
                a hover listener per anchor, so an overlapping card retriggers
                the sibling it covers and cards get stuck open. Here open state
                is owned entirely by this wrapper's hover, and the card is
                placed to the left of the icon column so it never covers a
                sibling icon.
              */}
              <span
                className='MemberSummaryPanel-info'
                onMouseEnter={() => setHoveredTier(tier.slug)}
                onMouseLeave={() => setHoveredTier(null)}
              >
                <button
                  type='button'
                  className='MemberSummaryPanel-info-btn cursor'
                  aria-label={`About ${displayTierName(tier.name)}`}
                  onFocus={() => setHoveredTier(tier.slug)}
                  onBlur={() => setHoveredTier(null)}
                >
                  <Image
                    src={Info}
                    alt=''
                    width={20}
                    height={20}
                  />
                </button>
                {hoveredTier === tier.slug && (
                  <div
                    className='MemberSummaryPanel-tip'
                    role='tooltip'
                  >
                    <TierTooltipContent
                      slug={tier.slug}
                      name={displayTierName(tier.name)}
                      price={tier.price.asFormattedString.split('.')[0]}
                    />
                  </div>
                )}
              </span>
            </label>
          ))}
        </fieldset>
      </CollapsibleSection>

      <CollapsibleSection
        title={<Text variant='title-xxs-500-upper'>Add-Ons</Text>}
        open={openSection === 'addons'}
        onRequestChange={toggle('addons')}
        testId='summary-section-addons'
      >
        <Text className='MemberSummaryPanel-section-desc'>
          Add Premier to your membership to experience an additional piece from
          our Premier closet.
        </Text>
        <fieldset className='MemberSummaryPanel-options'>
          <legend className='MemberSummaryPanel-legend-sr'>Add-ons</legend>
          <label className='MemberSummaryPanel-option cursor'>
            <input
              type='radio'
              name='addon'
              value='premier'
              checked={user.addon_subscription === 'premier'}
              onChange={() => onAddonChange('premier')}
              className='MemberSummaryPanel-radio'
            />
            <Text className='MemberSummaryPanel-option-label'>
              Premier {premierAddon?.price.asFormattedString.split('.')[0]}
              /month
            </Text>
          </label>
          <label className='MemberSummaryPanel-option cursor'>
            <input
              type='radio'
              name='addon'
              value='none'
              checked={user.addon_subscription !== 'premier'}
              onChange={() => onAddonChange('none')}
              className='MemberSummaryPanel-radio'
            />
            <Text className='MemberSummaryPanel-option-label'>None</Text>
          </label>
        </fieldset>
      </CollapsibleSection>

      <CollapsibleSection
        title={<Text variant='title-xxs-500-upper'>Coverage</Text>}
        open={openSection === 'coverage'}
        onRequestChange={toggle('coverage')}
        testId='summary-section-coverage'
      >
        <Text className='MemberSummaryPanel-section-desc'>
          Included coverage protects against normal wear &amp; tear. Upgrade to
          premium coverage for accidental damage protection.
        </Text>
        <fieldset className='MemberSummaryPanel-options'>
          <legend className='MemberSummaryPanel-legend-sr'>Coverage</legend>
          <label className='MemberSummaryPanel-option cursor'>
            <input
              type='radio'
              name='coverage'
              value='premium'
              checked={user.insurance_level === 'premium'}
              onChange={() => onCoverageChange('premium')}
              className='MemberSummaryPanel-radio'
            />
            <Text className='MemberSummaryPanel-option-label'>
              Premium {premiumInsurance?.price.asFormattedString.split('.')[0]}
              /month
            </Text>
          </label>
          <label className='MemberSummaryPanel-option cursor'>
            <input
              type='radio'
              name='coverage'
              value='standard'
              checked={user.insurance_level !== 'premium'}
              onChange={() => onCoverageChange('standard')}
              className='MemberSummaryPanel-radio'
            />
            <Text className='MemberSummaryPanel-option-label'>Standard</Text>
          </label>
        </fieldset>
      </CollapsibleSection>

      {/*
        Gift card redemption has no backing anywhere yet — no API, no EPCC
        integration, no redemption logic. The section is deliberately left as
        a slot until that groundwork exists.
      */}

      <CollapsibleSection
        title={<Text variant='title-xxs-500-upper'>Referral code</Text>}
        open={openSection === 'referral'}
        onRequestChange={toggle('referral')}
        testId='summary-section-referral'
      >
        <ReferralCodeField
          className='MemberSummaryPanel-referral'
          variant='summary'
          helperText='Enter your referral code'
          actionLabel='Update'
          referralCopy={referralCopy}
          appliedCode={user.referralCode}
          onCommit={onReferralCommit}
          data-testid='summary-referral-code'
        />
      </CollapsibleSection>
    </div>
  );
}

export default MemberSummaryPanel;
